/**
 * PLAT-11092 — the Video Clips grid's selection read, in one place.
 *
 * The selection is a `Set` of clip ids and a `Set` is insertion-ordered, so the
 * pick order is already there; what the grid needs is to read it rather than
 * walk the list. Both the reel Save as Reel builds and the numbers the cards
 * show come from the same call, so a card's number cannot state a position the
 * reel will not have (PLAT-11049, epic DE11).
 */

/** The selected clips, in the order they were picked, dropping ids no longer on screen. */
export function clipsInPickOrder<T extends { id?: string | null }>(
  clips: readonly T[],
  selectedIds: ReadonlySet<string>,
): T[] {
  const byId = new Map(clips.map(clip => [clip.id, clip]));
  return Array.from(selectedIds)
    .map(id => byId.get(id))
    .filter((clip): clip is T => !!clip);
}

/**
 * 1-based position per clip id. Taken from the ordered list rather than the
 * selection itself, which is what makes the numbers gapless: a pick that has
 * been filtered out of the grid is not going into the reel, so it must not hold
 * a number either.
 */
export function selectionPositionsFor(
  clipsInOrder: readonly { id?: string | null }[],
): Map<string, number> {
  const positions = new Map<string, number>();
  clipsInOrder.forEach(({ id }, index) => {
    if (id) positions.set(id, index + 1);
  });
  return positions;
}
